import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import IconButton from '@material-ui/core/IconButton';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import trashIcon from './trash.svg';
import styles from './styles';
import Grid from '@material-ui/core/Grid';

const VET_INSTITUTIONS = ['Small VET Clinic', 'Large VET Clinic'];

function isVetInstitution(institution) {
  return VET_INSTITUTIONS.includes(institution);
}

function getAvailableModalities(institution) {
  const standardModalities = ['CR', 'CT', 'DX', 'MG', 'MR', 'US', 'XA'];
  const vetModalities = ['CR', 'CT', 'DX', 'MR', 'US', 'XA'];

  return isVetInstitution(institution) ? vetModalities : standardModalities;
}

function getFullModalityName(modality) {
  switch (modality) {
    case 'CR': return 'Computed Radiography';
    case 'CT': return 'Computed Tomography';
    case 'DX': return 'Digital Radiography';
    case 'MG': return 'Mammography';
    case 'MR': return 'Magnetic Resonance';
    case 'US': return 'Ultrasound';
    case 'XA': return 'X-ray Angiography';
    default:   return '';
  }
}

const defaultDeviceParams = {
  'University or Large Hospital': {
    'CR': { studies_per_day: 180, size_per_study: 25 },
    'CT': { studies_per_day: 80, size_per_study: 500 },
    'DX': { studies_per_day: 260, size_per_study: 20 },
    'MG': { studies_per_day: 35, size_per_study: 250 },
    'MR': { studies_per_day: 35, size_per_study: 400 },
    'US': { studies_per_day: 90, size_per_study: 80 },
    'XA': { studies_per_day: 15, size_per_study: 1000 },
  },

  'Smaller or Community Hospital': {
    'CR': { studies_per_day: 70, size_per_study: 25 },
    'CT': { studies_per_day: 30, size_per_study: 350 },
    'DX': { studies_per_day: 100, size_per_study: 20 },
    'MG': { studies_per_day: 15, size_per_study: 220 },
    'MR': { studies_per_day: 15, size_per_study: 300 },
    'US': { studies_per_day: 40, size_per_study: 80 },
    'XA': { studies_per_day: 5, size_per_study: 700 },
  },

  'Radiology Imaging Center': {
    'CR': { studies_per_day: 45, size_per_study: 25 },
    'CT': { studies_per_day: 35, size_per_study: 450 },
    'DX': { studies_per_day: 80, size_per_study: 20 },
    'MG': { studies_per_day: 20, size_per_study: 250 },
    'MR': { studies_per_day: 25, size_per_study: 400 },
    'US': { studies_per_day: 45, size_per_study: 90 },
    'XA': { studies_per_day: 2, size_per_study: 700 },
  },

  'Orthopedic or Chiro Center': {
    'CR': { studies_per_day: 20, size_per_study: 20 },
    'CT': { studies_per_day: 12, size_per_study: 350 },
    'DX': { studies_per_day: 80, size_per_study: 20 },
    'MG': { studies_per_day: 2, size_per_study: 200 },
    'MR': { studies_per_day: 15, size_per_study: 350 },
    'US': { studies_per_day: 25, size_per_study: 80 },
    'XA': { studies_per_day: 1, size_per_study: 500 },
  },

  'Small VET Clinic': {
    'CR': { studies_per_day: 4, size_per_study: 20 },
    'CT': { studies_per_day: 1, size_per_study: 300 },
    'DX': { studies_per_day: 12, size_per_study: 20 },
    'MR': { studies_per_day: 1, size_per_study: 350 },
    'US': { studies_per_day: 7, size_per_study: 80 },
    'XA': { studies_per_day: 1, size_per_study: 300 },
  },

  'Large VET Clinic': {
    'CR': { studies_per_day: 12, size_per_study: 25 },
    'CT': { studies_per_day: 4, size_per_study: 500 },
    'DX': { studies_per_day: 35, size_per_study: 25 },
    'MR': { studies_per_day: 2, size_per_study: 500 },
    'US': { studies_per_day: 20, size_per_study: 100 },
    'XA': { studies_per_day: 1, size_per_study: 500 },
  },
};

const mobileTabletStyles = theme => ({
  mobileSectionHeader: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      minWidth: 'auto',
      maxWidth: '100%',
      backgroundColor: '#fff',
      borderRadius: 0,
      fontSize: '18px',
      padding: '16px',
      margin: '20px 0 0 0',
      boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
      display: 'flex',
      alignItems: 'center',
    },
  },
  mobileCardsOuter: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      maxWidth: '100%',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'flex-start',
      gap: 0,
      margin: '0 auto',
      padding: '0',
      overflowX: 'hidden',
    },
  },
  mobileCardsColumn: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      flex: 1,
      minWidth: 0,
      width: '50%',
      alignItems: 'center',
    },
  },
  mobileDeviceCard: {
    [theme.breakpoints.down('sm')]: {
      width: '90%',
      minWidth: '120px',
      maxWidth: '300px',
      margin: 0,
      padding: '14px 0',
      height: 'auto',
      borderRadius: 0,
      boxShadow: '0 1px 5px rgba(0,0,0,0.05)',
      fontSize: '15px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  mobileListContainer: {
    [theme.breakpoints.down('sm')]: {
      backgroundColor: '#fff',
      boxShadow: '0 2px 6px #E5E5EB',
      padding: '16px 8px 12px 8px',
      width: 'auto',
      minWidth: 'auto',
      maxWidth: '100%',
      margin: '20px 10px 40px 10px',
      maxHeight: 'none',
      overflowY: 'auto',
    },
  },
  mobileListRow: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      padding: '10px 0',
      margin: 0,
      borderBottom: '1px solid #D6DBE7',
      width: '100%',
      minHeight: 0,
      maxHeight: 'none',
      display: 'flex',
      justifyContent: 'space-between',
      gap: 0,
      background: 'none',
    },
  },
  mobileListMainInfo: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '4px',
      flexWrap: 'wrap',
      width: '100%',
      fontSize: '16px',
    },
  },
  mobileListDetails: {
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      width: '100%',
      fontSize: '15px',
      marginTop: 2,
      color: '#0F145C',
    },
  },
  mobileListActions: {
    [theme.breakpoints.down('sm')]: {
      alignSelf: 'center',
      marginLeft: '12px',
      marginTop: '4px',
      padding: 0,
    },
  },
  mobileIconImage: {
    [theme.breakpoints.down('sm')]: {
      width: 26,
      height: 26,
      display: 'block',
    },
  },
  mobileEmptyText: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '16px',
      color: '#9CA3AF',
      textAlign: 'center',
      padding: '10px 0 8px',
    },
  },
  mobileDialogPaper: {
    [theme.breakpoints.down('sm')]: {
      width: '94vw',
      minWidth: 0,
      maxWidth: '97vw',
      margin: '10px',
      borderRadius: 0,
    },
  },

  tabletCardsOuter: {
    [theme.breakpoints.down('lg')]: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '24px 24px',
      width: 'auto',
      maxWidth: '100vw',
      margin: '20px',
      padding: 0,
      boxSizing: 'border-box',
      overflowX: 'hidden',
      position: 'relative',
      left: '47.5%',
      transform: 'translate(-50%, 0)',
    },
  },
  tabletDeviceCard: {
    [theme.breakpoints.down('lg')]: {
      width: '100%',
      minWidth: 0,
      maxWidth: '100%',
      margin: 0,
      padding: '32px 0',
      height: 'auto',
      borderRadius: 0,
      boxShadow: '0 2px 12px rgba(0,0,0,0.11)',
      fontSize: '23px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      transition: 'all 0.2s cubic-bezier(.47,1.64,.41,.8)',
      cursor: 'pointer',
      boxSizing: 'border-box',
    },
  },
  tabletListContainer: {
    [theme.breakpoints.down('lg')]: {
      backgroundColor: '#fff',
      boxShadow: '0 2px 6px #E5E5EB',
      padding: '20px 16px 28px 16px',
      width: 'auto',
      maxWidth: '100%',
      minWidth: '0',
      margin: '28px 20px 40px 20px',
      maxHeight: 'none',
      borderRadius: 0,
      overflowY: 'auto',
      display: 'block',
      boxSizing: 'border-box',
    },
  },
  tabletListRow: {
    [theme.breakpoints.down('lg')]: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      padding: '18px 0',
      margin: 0,
      borderBottom: '1px solid #D6DBE7',
      width: '100%',
      minHeight: 0,
      maxHeight: 'none',
      display: 'flex',
      justifyContent: 'space-between',
      gap: 0,
      background: 'none',
    },
  },
  tabletListMainInfo: {
    [theme.breakpoints.down('lg')]: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '10px',
      flexWrap: 'wrap',
      width: '100%',
      fontSize: '19px',
    },
  },
  tabletListDetails: {
    [theme.breakpoints.down('lg')]: {
      display: 'block',
      width: '100%',
      fontSize: '18px',
      marginTop: 3,
      color: '#0F145C',
    },
  },
  tabletListActions: {
    [theme.breakpoints.down('lg')]: {
      alignSelf: 'center',
      marginLeft: '20px',
      marginTop: '10px',
      padding: 0,
    },
  },
  tabletIconImage: {
    [theme.breakpoints.down('lg')]: {
      width: 34,
      height: 34,
      display: 'block',
    },
  },
  tabletEmptyText: {
    [theme.breakpoints.down('lg')]: {
      fontSize: '19px',
      color: '#9CA3AF',
      textAlign: 'center',
      padding: '16px 0 10px',
    },
  },
  tabletDialogPaper: {
    [theme.breakpoints.down('lg')]: {
      width: '95vw',
      minWidth: 0,
      maxWidth: '500px',
      margin: '20px auto',
      borderRadius: 0,
    },
  },

  stepBoxContainer: {
    background: '#ffffff',
    padding: '22px 28px',
    borderRadius: 0,
    maxWidth: '1320px',
    margin: '40px 0px 14px 15px',
    boxShadow: '0 2px 8px rgba(27,52,123,.07)',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  mobileStepBoxContainer: {
    [theme.breakpoints.down('sm')]: {
      width: 'auto',
      padding: '10px 12px',
      borderRadius: 0,
      fontSize: '16px',
      margin: '16px 10px 10px 10px',
    },
  },
  tabletStepBoxContainer: {
    [theme.breakpoints.down('lg')]: {
      width: 'auto',
      padding: '14px 18px',
      borderRadius: 0,
      fontSize: '19px',
      margin: '22px 20px 12px 20px',
    },
  },
});

const mergedStyles = theme => ({
  ...styles(theme),
  ...mobileTabletStyles(theme),
});

function CustomDialog({
  open,
  handleClose,
  modality,
  studySize,
  updateInputSize,
  studiesDay,
  updateInputStudies,
  handleAddClick,
  classes,
  isMobile,
  isTablet,
}) {
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={{
        paper: [
          classes.dialogPaper,
          isMobile && classes.mobileDialogPaper,
          isTablet && classes.tabletDialogPaper,
        ].filter(Boolean).join(' ')
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <div style={{ fontSize: '22pt', fontWeight: 'bold', color: '#0F145C' }}>
          {modality || 'Device'} – {getFullModalityName(modality)}
        </div>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <div
          style={{
            fontWeight: 'bold',
            color: '#0F145C',
            marginBottom: '1px',
            fontSize: '13pt',
          }}
        >
          Study Size in MB:
        </div>
        <TextField
          type="number"
          variant="outlined"
          value={studySize || ''}
          onChange={updateInputSize}
          fullWidth
          margin="dense"
          classes={{ root: classes.blueFieldRoot }}
          InputProps={{
            disableUnderline: true,
            classes: { input: classes.numberInputBlue },
          }}
        />
        <div
          style={{
            fontWeight: 'bold',
            color: '#0F145C',
            marginTop: '16px',
            marginBottom: '1px',
            fontSize: '13pt',
          }}
        >
          Studies per Day:
        </div>
        <TextField
          type="number"
          variant="outlined"
          value={studiesDay || ''}
          onChange={updateInputStudies}
          fullWidth
          margin="dense"
          classes={{ root: classes.orangeFieldRoot }}
          InputProps={{
            disableUnderline: true,
            classes: { input: classes.numberInputOrange },
          }}
        />
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button onClick={handleClose} className={classes.cancelButton}>
          Cancel
        </Button>
        <Button onClick={handleAddClick} className={classes.addButton}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}

class Devices extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      modality: '',
      studiesDay: '',
      studySize: '',
      devices: [],
      selectedDevice: [],
      open: false,
      isMobile: window.innerWidth <= 600,
      isTablet: window.innerWidth > 600 && window.innerWidth <= 1280,
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const institution = nextProps.selectedIns || 'University or Large Hospital';
    const defaultParams =
      defaultDeviceParams[institution] ||
      defaultDeviceParams['University or Large Hospital'];

    const modalities = getAvailableModalities(institution);

    const newDevices = modalities.map((modality, index) => ({
      id: index + 1,
      modality: modality,
      studies_per_day: defaultParams[modality].studies_per_day,
      size_per_study: defaultParams[modality].size_per_study,
    }));

    if (JSON.stringify(newDevices) !== JSON.stringify(prevState.devices)) {
      let updateDialog = {};

      if (prevState.open && prevState.modality) {
        if (modalities.includes(prevState.modality) && defaultParams[prevState.modality]) {
          updateDialog = {
            studySize: defaultParams[prevState.modality].size_per_study,
            studiesDay: defaultParams[prevState.modality].studies_per_day,
          };
        } else {
          updateDialog = {
            open: false,
            modality: '',
            studySize: '',
            studiesDay: '',
          };
        }
      }

      return { devices: newDevices, ...updateDialog };
    }

    return null;
  }

  handleResize = () => {
    const width = window.innerWidth;
    const isMobile = width <= 600;
    const isTablet = width > 600 && width <= 1280;
    if (isMobile !== this.state.isMobile || isTablet !== this.state.isTablet) {
      this.setState({ isMobile, isTablet });
    }
  };

  componentDidMount() {
    this.props.changeActiveStep(1);
    window.addEventListener('resize', this.handleResize);
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleOpen = (modality, size, studies) => {
    this.setState({
      open: true,
      modality: modality,
      studySize: size,
      studiesDay: studies,
    });
  };

  handleClose = () => {
    this.setState({
      open: false,
      studiesDay: null,
      studySize: null,
    });
  };

  handleAddClick = (e) => {
    e.preventDefault();
    const deviceJSON = this.getJSON();
    this.setState(
      prev => ({ items: [...prev.items, deviceJSON] }),
      () => {
        this.updateDevices(deviceJSON);
        this.handleClose();
      }
    );
    return false;
  };

  handleRemoveClick = (dev) => {
    this.props.removeDevice(dev);
  };

  getJSON = () => {
    const deviceJSON = {
      id: this.generateID(),
      name: this.state.modality,
      studies_per_day: this.state.studiesDay,
      size_per_study: this.state.studySize,
    };
    this.setState({ selectedDevice: deviceJSON });
    return deviceJSON;
  };

  generateID = () => Math.floor(Math.random() * 10000);

  updateInputStudies = (event) => {
    const value = event.target.value;
    this.setState({ studiesDay: value.startsWith('-') ? value.slice(1) : value });
  };

  updateInputSize = (event) => {
    const value = event.target.value;
    this.setState({ studySize: value.startsWith('-') ? value.slice(1) : value });
  };

  updateDevices = (device) => {
    this.props.changeDevices(device);
  };

  renderDeviceCardsMobile() {
    const { classes } = this.props;
    const col1 = this.state.devices.slice(0, 4);
    const col2 = this.state.devices.slice(4, 7);
    return (
      <div className={classes.mobileCardsOuter}>
        <div className={classes.mobileCardsColumn}>
          {col1.map(dev => (
            <Paper
              key={dev.id}
              className={classes.mobileDeviceCard}
              onClick={() =>
                this.handleOpen(
                  dev.modality,
                  dev.size_per_study,
                  dev.studies_per_day
                )
              }
              elevation={0}
            >
              <div style={{ fontWeight: 600, color: '#0F145C', fontSize: '17px' }}>{dev.modality}</div>
              <div style={{ fontSize: '12px', color: '#6B7280', textAlign: 'center' }}>
                {getFullModalityName(dev.modality)}
              </div>
            </Paper>
          ))}
        </div>
        <div className={classes.mobileCardsColumn}>
          {col2.map(dev => (
            <Paper
              key={dev.id}
              className={classes.mobileDeviceCard}
              onClick={() =>
                this.handleOpen(
                  dev.modality,
                  dev.size_per_study,
                  dev.studies_per_day
                )
              }
              elevation={0}
            >
              <div style={{ fontWeight: 600, color: '#0F145C', fontSize: '17px' }}>{dev.modality}</div>
              <div style={{ fontSize: '12px', color: '#6B7280', textAlign: 'center' }}>
                {getFullModalityName(dev.modality)}
              </div>
            </Paper>
          ))}
        </div>
      </div>
    );
  }

  renderDeviceCardsTablet() {
    const { classes } = this.props;
    return (
      <div className={classes.tabletCardsOuter}>
        {this.state.devices.map(dev => (
          <Paper
            key={dev.id}
            className={classes.tabletDeviceCard}
            onClick={() =>
              this.handleOpen(
                dev.modality,
                dev.size_per_study,
                dev.studies_per_day
              )
            }
            elevation={0}
          >
            <div style={{ fontWeight: 700, color: '#0F145C', fontSize: '28px', letterSpacing: 2 }}>{dev.modality}</div>
            <div style={{ fontSize: '16px', color: '#6B7280', textAlign: 'center', marginTop: 6 }}>
              {getFullModalityName(dev.modality)}
            </div>
          </Paper>
        ))}
      </div>
    );
  }

  renderDeviceCardsDesktop() {
    const { classes } = this.props;
    return (
      <div className={classes.cardsContainer}>
        {this.state.devices.map(dev => {
          const isActive =
            this.state.modality === dev.modality && !this.state.open;
          return (
            <Paper
              key={dev.id}
              className={[
                classes.deviceCard,
                isActive ? classes.activeCard : '',
              ].join(' ')}
              onClick={() =>
                this.handleOpen(
                  dev.modality,
                  dev.size_per_study,
                  dev.studies_per_day
                )
              }
              elevation={0}
            >
              <div className={classes.cardSymbol}>{dev.modality}</div>
              <div className={classes.cardLabel}>
                {getFullModalityName(dev.modality)}
              </div>
            </Paper>
          );
        })}
      </div>
    );
  }

  renderListMobile() {
    const { classes, getSelectedDevices } = this.props;
    return (
      <div className={classes.mobileListContainer}>
        <List disablePadding>
          {getSelectedDevices.length === 0 ? (
            <div className={classes.mobileEmptyText}>No devices added</div>
          ) : (
            getSelectedDevices.map((dev, idx) => (
              <ListItem
                key={dev.id}
                className={classes.mobileListRow}
                disableGutters
              >
                <div style={{ flex: 1 }}>
                  <div className={classes.mobileListMainInfo}>
                    <span style={{ color: '#F97316', fontWeight: 700, fontSize: 17 }}>{idx + 1}.</span>
                    <span style={{ color: '#0F145C', fontWeight: 600, fontSize: 16, marginRight: 2 }}>{dev.name}</span>
                    <span style={{ color: '#0F145C', fontWeight: 400, fontSize: 15, marginRight: 2 }}>–</span>
                    <span style={{ color: '#0F145C', fontWeight: 600, fontSize: 15 }}>{getFullModalityName(dev.name)}</span>
                  </div>
                  <div className={classes.mobileListDetails}>
                    Study size: <b>{dev.size_per_study}MB</b><br />
                    Studies per day: <b>{dev.studies_per_day}</b>
                  </div>
                </div>
                <div className={classes.mobileListActions}>
                  <IconButton
                    onClick={() => this.handleRemoveClick(dev)}
                    style={{ padding: 4 }}
                  >
                    <img src={trashIcon} alt="Delete" className={classes.mobileIconImage} />
                  </IconButton>
                </div>
              </ListItem>
            ))
          )}
        </List>
      </div>
    );
  }

  renderListTablet() {
    const { classes, getSelectedDevices } = this.props;
    return (
      <div className={classes.tabletListContainer}>
        <List disablePadding>
          {getSelectedDevices.length === 0 ? (
            <div className={classes.tabletEmptyText}>No devices added</div>
          ) : (
            getSelectedDevices.map((dev, idx) => (
              <ListItem
                key={dev.id}
                className={classes.tabletListRow}
                disableGutters
              >
                <div style={{ flex: 1 }}>
                  <div className={classes.tabletListMainInfo}>
                    <span style={{ color: '#F97316', fontWeight: 700, fontSize: 18 }}>{idx + 1}.</span>
                    <span style={{ color: '#0F145C', fontWeight: 600, fontSize: 17, marginRight: 3 }}>{dev.name}</span>
                    <span style={{ color: '#0F145C', fontWeight: 400, fontSize: 15, marginRight: 3 }}>–</span>
                    <span style={{ color: '#0F145C', fontWeight: 600, fontSize: 16 }}>{getFullModalityName(dev.name)}</span>
                  </div>
                  <div className={classes.tabletListDetails}>
                    Study size: <b>{dev.size_per_study}MB</b><br />
                    Studies per day: <b>{dev.studies_per_day}</b>
                  </div>
                </div>
                <div className={classes.tabletListActions}>
                  <IconButton
                    onClick={() => this.handleRemoveClick(dev)}
                    style={{ padding: 4 }}
                  >
                    <img src={trashIcon} alt="Delete" className={classes.tabletIconImage} />
                  </IconButton>
                </div>
              </ListItem>
            ))
          )}
        </List>
      </div>
    );
  }

  renderListDesktop() {
    const { classes, getSelectedDevices } = this.props;
    return (
      <div className={classes.listContainer}>
        <List disablePadding>
          {getSelectedDevices.length === 0 ? (
            <div className={classes.emptyText}>No devices added</div>
          ) : (
            getSelectedDevices.map((dev, idx) => (
              <ListItem
                key={dev.id}
                className={classes.listRow}
                disableGutters
              >
                <div className={classes.listLeft}>
                  <span className={classes.listIndex}>{idx + 1}.</span>
                  <span className={classes.listName}>{dev.name}</span>
                  <span className={classes.listSeparator}> – </span>
                  <span className={classes.listName}>
                    {getFullModalityName(dev.name)}
                  </span>
                  <span className={classes.listSeparator}> | </span>
                  <span className={classes.listSub}>
                    Study size: {dev.size_per_study} MB
                  </span>
                  <span className={classes.listSeparator}> | </span>
                  <span className={classes.listSub}>
                    Studies per day: {dev.studies_per_day}
                  </span>
                </div>
                <div className={classes.listActions}>
                  <IconButton
                    onClick={() => this.handleRemoveClick(dev)}
                    className={classes.menuButton}
                  >
                    <img src={trashIcon} alt="Delete" className={classes.iconImage} />
                  </IconButton>
                </div>
              </ListItem>
            ))
          )}
        </List>
      </div>
    );
  }

  render() {
    const { classes } = this.props;
    const { isMobile, isTablet } = this.state;

    const stepBoxClass = [
      classes.stepBoxContainer,
      isMobile && classes.mobileStepBoxContainer,
      isTablet && classes.tabletStepBoxContainer,
    ].filter(Boolean).join(' ');

    const stepBoxTextStyle = isMobile
      ? { margin: 0, fontSize: '16px', color: '#0F145C' }
      : { margin: 0, fontSize: '24px', color: '#0F145C' };

    return (
      <div>
        <Grid container direction="row" className={stepBoxClass}>
          <p style={stepBoxTextStyle}>
            <strong>Step&nbsp;2:</strong> Click a device card to view and edit its details.
          </p>
        </Grid>
        {(isMobile)
          ? this.renderDeviceCardsMobile()
          : isTablet
          ? this.renderDeviceCardsTablet()
          : this.renderDeviceCardsDesktop()
        }

        <Grid container direction="row" className={stepBoxClass}>
          <p style={stepBoxTextStyle}>
            <strong>Step&nbsp;3:</strong> Manage your selected devices here. You can delete your devices.
          </p>
        </Grid>
        {(isMobile)
          ? this.renderListMobile()
          : isTablet
          ? this.renderListTablet()
          : this.renderListDesktop()
        }

        <CustomDialog
          open={this.state.open}
          handleClose={this.handleClose}
          modality={this.state.modality}
          studySize={this.state.studySize}
          updateInputSize={this.updateInputSize}
          studiesDay={this.state.studiesDay}
          updateInputStudies={this.updateInputStudies}
          handleAddClick={this.handleAddClick}
          classes={classes}
          isMobile={isMobile}
          isTablet={isTablet}
        />
      </div>
    );
  }
}

Devices.propTypes = {
  classes: PropTypes.object.isRequired,
  getSelectedDevices: PropTypes.array.isRequired,
  changeActiveStep: PropTypes.func.isRequired,
  removeDevice: PropTypes.func.isRequired,
  changeDevices: PropTypes.func.isRequired,
  selectedIns: PropTypes.string,
};

Devices.defaultProps = {
  getSelectedDevices: [],
  changeActiveStep: () => {},
  removeDevice: () => {},
  changeDevices: () => {},
  selectedIns: 'University or Large Hospital',
};

export default withStyles(mergedStyles)(Devices);


// WEBPACK FOOTER //
// ./src/Devices.js